import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Field, inputCls } from "@/components/ui-primitives";
import { Trash2, Camera } from "lucide-react";
import PhotoUpload from "@/components/PhotoUpload";

const emptyForm = {
  equipment_id: "",
  date: new Date().toISOString().split("T")[0],
  fault: "",
  cause: "",
  repair: "",
  parts: "",
  status: "Open",
};

export default function Faults() {
  const [equipment, setEquipment] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [attachments, setAttachments] = useState([]);

  const load = async () => {
    try {
      const [eqRes, fRes] = await Promise.all([api.get("/equipment"), api.get("/faults")]);
      setEquipment(eqRes.data);
      setItems(fRes.data);
      if (!form.equipment_id && eqRes.data.length > 0) {
        setForm((f) => ({ ...f, equipment_id: eqRes.data[0].id }));
      }
    } catch { toast.error("Failed to load"); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.equipment_id || !form.fault.trim()) {
      toast.error("Equipment and Fault description required");
      return;
    }
    try {
      await api.post("/faults", { ...form, attachments });
      toast.success("Fault logged");
      setForm({ ...emptyForm, equipment_id: form.equipment_id });
      setAttachments([]);
      load();
    } catch { toast.error("Save failed"); }
  };

  const toggleStatus = async (f) => {
    try {
      const newStatus = f.status === "Open" ? "Closed" : "Open";
      await api.put(`/faults/${f.id}`, { status: newStatus });
      toast.success(`Marked as ${newStatus}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this fault record?")) return;
    try {
      await api.delete(`/faults/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const equipmentLabel = (id) => {
    const e = equipment.find((x) => x.id === id);
    return e ? `${e.type} · ${e.asset_id}` : "—";
  };

  return (
    <div className="p-8 max-w-[1800px] mx-auto">
      <PageHeader eyebrow="Incidents" title="Breakdowns" accent="/ Faults" />

      <form onSubmit={submit} className="bg-[#121212] border border-white/10 p-5 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-5">
          <Field label="Equipment *">
            <select data-testid="fault-equipment" value={form.equipment_id} onChange={(e) => setForm({ ...form, equipment_id: e.target.value })} className={inputCls}>
              {equipment.length === 0 && <option value="">— No equipment —</option>}
              {equipment.map((e) => (<option key={e.id} value={e.id}>{e.type} · {e.asset_id}</option>))}
            </select>
          </Field>
          <Field label="Date *">
            <input type="date" data-testid="fault-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Fault *">
            <input data-testid="fault-desc" value={form.fault} onChange={(e) => setForm({ ...form, fault: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Cause">
            <input data-testid="fault-cause" value={form.cause} onChange={(e) => setForm({ ...form, cause: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Repair">
            <input data-testid="fault-repair" value={form.repair} onChange={(e) => setForm({ ...form, repair: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Parts Used">
            <input data-testid="fault-parts" value={form.parts} onChange={(e) => setForm({ ...form, parts: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Status">
            <select data-testid="fault-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
              <option>Open</option>
              <option>Closed</option>
            </select>
          </Field>
          <div className="flex items-end">
            <button data-testid="fault-save-btn" type="submit" className="w-full px-5 py-2.5 bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-medium">
              Save Fault
            </button>
          </div>
        </div>
        <div>
          <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-2 flex items-center gap-2">
            <Camera className="w-3.5 h-3.5" /> Photo Evidence (optional)
          </div>
          <PhotoUpload testid="fault-photos" value={attachments} onChange={setAttachments} />
        </div>
      </form>

      <div className="border border-white/10 bg-[#121212] overflow-x-auto">
        <table data-testid="fault-table" className="w-full text-sm">
          <thead className="bg-black/60 backdrop-blur-xl">
            <tr className="text-left font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">
              <th className="px-4 py-3 border-b border-white/10">Date</th>
              <th className="px-4 py-3 border-b border-white/10">Equipment</th>
              <th className="px-4 py-3 border-b border-white/10">Fault</th>
              <th className="px-4 py-3 border-b border-white/10">Cause</th>
              <th className="px-4 py-3 border-b border-white/10">Repair</th>
              <th className="px-4 py-3 border-b border-white/10">Parts</th>
              <th className="px-4 py-3 border-b border-white/10 text-center">Photos</th>
              <th className="px-4 py-3 border-b border-white/10">Status</th>
              <th className="px-4 py-3 border-b border-white/10 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (<tr><td colSpan={9} className="px-4 py-12 text-center text-[#A3A3A3]">No faults recorded.</td></tr>)}
            {items.map((f) => {
              const isOpen = f.status === "Open";
              return (
                <tr key={f.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                  <td className="px-4 py-3 font-mono-tab text-xs text-[#E0E0E0]">{f.date}</td>
                  <td className="px-4 py-3 text-white">{equipmentLabel(f.equipment_id)}</td>
                  <td className="px-4 py-3 text-[#E0E0E0]">{f.fault}</td>
                  <td className="px-4 py-3 text-[#A3A3A3] text-xs">{f.cause || "—"}</td>
                  <td className="px-4 py-3 text-[#A3A3A3] text-xs">{f.repair || "—"}</td>
                  <td className="px-4 py-3 text-[#A3A3A3] text-xs">{f.parts || "—"}</td>
                  <td className="px-4 py-3 text-center font-mono-tab text-xs">
                    {f.attachments?.length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[#007AFF]">
                        <Camera className="w-3 h-3" /> {f.attachments.length}
                      </span>
                    ) : <span className="text-[#A3A3A3]">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`fault-toggle-${f.id}`}
                      onClick={() => toggleStatus(f)}
                      className={`inline-block px-2 py-0.5 text-[10px] font-mono-tab tracking-[0.15em] uppercase border transition-all ${
                        isOpen
                          ? "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30 hover:bg-[#FF3B30]/25"
                          : "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30 hover:bg-[#22C55E]/25"
                      }`}
                    >
                      {f.status}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button data-testid={`fault-del-${f.id}`} onClick={() => remove(f.id)} className="p-1.5 border border-white/10 hover:border-[#FF3B30]/40 hover:bg-[#FF3B30]/10">
                        <Trash2 className="w-3.5 h-3.5 text-[#FF3B30]" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
